// @ts-nocheck
/**
 * Central data provider for the customizable dashboard.
 *
 * The v1-verbatim DashboardPage.tsx owned all the state + API calls for
 * a handful of widgets that couldn't fetch their own data. The new
 * widget-grid framework renders widgets independently, so we hoist that
 * shared state into a Context and let the two prop-driven widgets
 * (DashboardCallsChartsWidget, AgentStatusDistributionWidget) pull
 * from it via `useDashboardData()`.
 *
 * State + effects preserved byte-identically from the legacy
 * DashboardPage — including the date-clamp calc, portal-report rate
 * limiter, and 3-second bootstrap batch — so widget behavior is
 * unchanged.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { getApiCallShared } from '@/service/sharedGet';
import {
  API_AGENTS_LIST,
  API_DASHBOARD_ANSWERED_UNANSWERED_SUMMARY,
  API_DASHBOARD_DISCONNECTED_CALL_SUMMARY,
  API_DASHBOARD_INCOMING_SUMMARY,
  API_DASHBOARD_OUTBOUND_CAMPAIGNS_STATS,
  API_DASHBOARD_OUTGOING_CALLS_SUMMARY,
  API_DASHBOARD_STATS,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { loadPortalReportData, PORTAL_REPORT_RESOURCES } from '@/utilities/portalReportRefresh';
import {
  PRIMARY_COLOR,
  SKY_COLOR,
  EMERALD_COLOR,
  VIOLET_COLOR,
  AMBER_COLOR,
  ROSE_COLOR,
} from './chart-palette';

const DASHBOARD_MAX_INCLUSIVE_DAYS = 180;

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number) {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
}

function getInclusiveDayCount(from: Date, to: Date) {
  const s = startOfDay(from).getTime();
  const e = startOfDay(to).getTime();
  return Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
}

function formatDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const DashboardDataContext = createContext<any>(null);

export function useDashboardData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) throw new Error('useDashboardData must be used inside <DashboardDataProvider>');
  return ctx;
}

export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const [loggedInUserId, setLoggedInUserId] = useState<string | null>(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState<string | null>(null);

  const [startDate, setStartDate] = useState<Date | null>(() => startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(() => startOfDay(new Date()));

  const [agentDataObject, setAgentDataObject] = useState({
    total_agent: 0,
    logged_in_agent: 0,
    agent_on_call: 0,
  });
  const [incomingSummaryData, setIncomingSummaryData] = useState({
    dialed_call: 0,
    answered_call: 0,
    abandoned_calls: 0,
  });
  const [outgoingCallsData, setOutgoingCallsData] = useState({
    total_calls: 0,
    dialed_call: 0,
    answered_call: 0,
  });
  const [answeredUnansweredSummary, setAnsweredUnansweredSummary] = useState<any[]>([]);
  const [answeredUnansweredSummaryLoading, setAnsweredUnansweredSummaryLoading] = useState(false);
  const [loadingForAgentStatusDistribution, setLoadingForAgentStatusDistribution] = useState(true);

  // KPI-strip data (Phase C). Tab widgets keep fetching independently for
  // their own refresh + rate-limit UI. The extra GETs on load are trivial.
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [campaignStats, setCampaignStats] = useState<any>(null);
  const [disconnectedSummary, setDisconnectedSummary] = useState<any>(null);
  const [kpiLoading, setKpiLoading] = useState(true);

  // Identity resolution — Storage.user matches how the whole CTI subsystem reads role.
  useEffect(() => {
    const u = getUser();
    if (!u) return;
    setLoggedInUserId(u.id ?? null);
    setLoggedInUserRole(u.role ?? null);
  }, []);

  // Date clamp derivations (byte-identical from legacy DashboardPage).
  const maxSelectableDate = startOfDay(new Date());
  const minSelectableDate = addDays(maxSelectableDate, -(DASHBOARD_MAX_INCLUSIVE_DAYS - 1));

  let dashboardStartCalendarMinDate = minSelectableDate;
  let dashboardStartCalendarMaxDate = maxSelectableDate;
  if (endDate) {
    const latestStartForEnd = addDays(endDate, -(DASHBOARD_MAX_INCLUSIVE_DAYS - 1));
    dashboardStartCalendarMinDate =
      latestStartForEnd.getTime() > minSelectableDate.getTime()
        ? latestStartForEnd
        : minSelectableDate;
    const endDay = startOfDay(endDate);
    dashboardStartCalendarMaxDate =
      endDay.getTime() < maxSelectableDate.getTime() ? endDay : maxSelectableDate;
  }
  if (dashboardStartCalendarMinDate.getTime() > dashboardStartCalendarMaxDate.getTime()) {
    dashboardStartCalendarMinDate = dashboardStartCalendarMaxDate;
  }

  let dashboardEndCalendarMinDate = minSelectableDate;
  let dashboardEndCalendarMaxDate = maxSelectableDate;
  if (startDate) {
    const earliestEndForStart = startOfDay(startDate);
    dashboardEndCalendarMinDate =
      earliestEndForStart.getTime() > minSelectableDate.getTime()
        ? earliestEndForStart
        : minSelectableDate;
    const maxEndForStart = addDays(startDate, DASHBOARD_MAX_INCLUSIVE_DAYS - 1);
    dashboardEndCalendarMaxDate =
      maxEndForStart.getTime() < maxSelectableDate.getTime() ? maxEndForStart : maxSelectableDate;
  }
  if (dashboardEndCalendarMinDate.getTime() > dashboardEndCalendarMaxDate.getTime()) {
    dashboardEndCalendarMinDate = dashboardEndCalendarMaxDate;
  }

  const handleDashboardStartDateChange = (e: any) => {
    let next: Date | null = e.value ? startOfDay(e.value) : null;
    if (!next) {
      setStartDate(null);
      return;
    }
    if (next < minSelectableDate) next = minSelectableDate;
    if (next > maxSelectableDate) next = maxSelectableDate;
    setStartDate(next);
    setEndDate((prev) => {
      let end = prev ? startOfDay(prev) : next!;
      let upper = addDays(next!, DASHBOARD_MAX_INCLUSIVE_DAYS - 1);
      if (upper > maxSelectableDate) upper = maxSelectableDate;
      if (end < next!) end = next!;
      if (end > upper) end = upper;
      return end;
    });
  };

  const handleDashboardEndDateChange = (e: any) => {
    let next: Date | null = e.value ? startOfDay(e.value) : null;
    if (!next) {
      setEndDate(null);
      return;
    }
    if (next < minSelectableDate) next = minSelectableDate;
    if (next > maxSelectableDate) next = maxSelectableDate;
    setEndDate(next);
    setStartDate((prev) => {
      let s = prev ? startOfDay(prev) : next!;
      let lower = addDays(next!, -(DASHBOARD_MAX_INCLUSIVE_DAYS - 1));
      if (lower < minSelectableDate) lower = minSelectableDate;
      if (s < lower) s = lower;
      if (s > next!) s = next!;
      return s;
    });
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    const today = startOfDay(new Date());
    let upper = addDays(startDate, DASHBOARD_MAX_INCLUSIVE_DAYS - 1);
    if (upper.getTime() > today.getTime()) upper = today;
    const e = startOfDay(endDate);
    const s = startOfDay(startDate);
    if (e.getTime() < s.getTime()) {
      setEndDate(s);
      return;
    }
    if (e.getTime() > upper.getTime()) {
      setEndDate(upper);
    }
  }, [startDate, endDate]);

  const reportRefreshBlocked = (label: string, remainingMs: number) => {
    toast.info(`${label} can be refreshed after ${Math.ceil(remainingMs / 1000)} seconds.`);
  };

  // force=false on first load: shares the agents GET with dashboard widgets.
  const fetchAgents = (force = true) => {
    setLoadingForAgentStatusDistribution(true);
    (force ? getApiCall(API_AGENTS_LIST) : getApiCallShared(API_AGENTS_LIST))
      .then((resp: any) => {
        if (resp && Array.isArray(resp)) {
          const total_agent = resp.length;
          const logged_in_agent = resp.filter((a: any) => a.login_status === '1').length;
          const agent_on_call = resp.filter((a: any) => Number(a.call_status) === 1).length;
          setAgentDataObject({ total_agent, logged_in_agent, agent_on_call });
        }
        setLoadingForAgentStatusDistribution(false);
      })
      .catch((error: any) => {
        console.log(error);
        setLoadingForAgentStatusDistribution(false);
      });
  };

  const fetchIncomingSummary = async (force = false) => {
    if (!loggedInUserId || !loggedInUserRole) return;
    const today = new Date().toISOString().split('T')[0];
    try {
      const result = await loadPortalReportData({
        resource: PORTAL_REPORT_RESOURCES.INCOMING_SUMMARY_DASHBOARD,
        userId: loggedInUserId,
        role: loggedInUserRole,
        params: { startDate: today, endDate: today },
        force,
        onBlocked: (ms: number) => reportRefreshBlocked('Incoming summary', ms),
        fetchFn: () =>
          getApiCall(`${API_DASHBOARD_INCOMING_SUMMARY}?startDate=${today}&endDate=${today}`),
      });
      setIncomingSummaryData({
        dialed_call: Number(result.data?.dialed_calls) || 0,
        answered_call: Number(result.data?.answered_calls) || 0,
        abandoned_calls: Number(result.data?.abandoned_calls) || 0,
      });
    } catch (error) {
      console.error('Error fetching today’s data:', error);
    }
  };

  const fetchOutgoingCallsData = async (force = false) => {
    if (!loggedInUserId || !loggedInUserRole) return;
    const today = new Date();
    const s = formatDate(today);
    try {
      const result = await loadPortalReportData({
        resource: PORTAL_REPORT_RESOURCES.OUTGOING_SUMMARY_DASHBOARD,
        userId: loggedInUserId,
        role: loggedInUserRole,
        params: { startDate: s, endDate: s },
        force,
        onBlocked: (ms: number) => reportRefreshBlocked('Outgoing summary', ms),
        fetchFn: () =>
          getApiCall(`${API_DASHBOARD_OUTGOING_CALLS_SUMMARY}?startDate=${s}&endDate=${s}`),
      });
      setOutgoingCallsData({
        total_calls: Number(result.data?.total_calls) || 0,
        dialed_call: Number(result.data?.dialed_calls) || 0,
        answered_call: Number(result.data?.answered_calls) || 0,
      });
    } catch (error) {
      console.error("Error fetching today's outgoing calls", error);
    }
  };

  const fetchAnsweredUnansweredSummary = async (force = false) => {
    if (!loggedInUserId || !loggedInUserRole) return;
    setAnsweredUnansweredSummaryLoading(true);
    try {
      const result = await loadPortalReportData({
        resource: PORTAL_REPORT_RESOURCES.ANSWERED_UNANSWERED_SUMMARY_DASHBOARD,
        userId: loggedInUserId,
        role: loggedInUserRole,
        params: { range: 'fixed-periods' },
        force,
        onBlocked: (ms: number) => reportRefreshBlocked('Answered/unanswered summary', ms),
        fetchFn: () => getApiCall(API_DASHBOARD_ANSWERED_UNANSWERED_SUMMARY),
      });
      setAnsweredUnansweredSummary(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      console.error('Error fetching answered/unanswered summary', error);
    } finally {
      setAnsweredUnansweredSummaryLoading(false);
    }
  };

  const handleFilter = async () => {
    if (!startDate || !endDate) {
      console.warn('Please select both start and end dates');
      return;
    }
    if (startOfDay(startDate) > startOfDay(endDate)) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    if (getInclusiveDayCount(startDate, endDate) > DASHBOARD_MAX_INCLUSIVE_DAYS) {
      toast.error(
        `Date range too large. Maximum allowed is ${DASHBOARD_MAX_INCLUSIVE_DAYS} calendar days (inclusive).`,
      );
      return;
    }
    if (!loggedInUserId || !loggedInUserRole) return;

    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);

    try {
      const [incomingResult, outgoingResult] = await Promise.all([
        loadPortalReportData({
          resource: PORTAL_REPORT_RESOURCES.INCOMING_SUMMARY_DASHBOARD,
          userId: loggedInUserId,
          role: loggedInUserRole,
          params: { startDate: formattedStart, endDate: formattedEnd },
          force: true,
          onBlocked: (ms: number) => reportRefreshBlocked('Incoming summary', ms),
          fetchFn: () =>
            getApiCall(
              `${API_DASHBOARD_INCOMING_SUMMARY}?startDate=${formattedStart}&endDate=${formattedEnd}`,
            ),
        }),
        loadPortalReportData({
          resource: PORTAL_REPORT_RESOURCES.OUTGOING_SUMMARY_DASHBOARD,
          userId: loggedInUserId,
          role: loggedInUserRole,
          params: { startDate: formattedStart, endDate: formattedEnd },
          force: true,
          onBlocked: (ms: number) => reportRefreshBlocked('Outgoing summary', ms),
          fetchFn: () =>
            getApiCall(
              `${API_DASHBOARD_OUTGOING_CALLS_SUMMARY}?startDate=${formattedStart}&endDate=${formattedEnd}`,
            ),
        }),
      ]);

      setIncomingSummaryData({
        dialed_call: Number(incomingResult.data?.dialed_calls) || 0,
        answered_call: Number(incomingResult.data?.answered_calls) || 0,
        abandoned_calls: Number(incomingResult.data?.abandoned_calls) || 0,
      });
      setOutgoingCallsData({
        total_calls: Number(outgoingResult.data?.total_calls) || 0,
        dialed_call: Number(outgoingResult.data?.dialed_calls) || 0,
        answered_call: Number(outgoingResult.data?.answered_calls) || 0,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // 3-second bootstrap batch — matches the v1 verbatim timing so any
  // downstream API rate-limits assume the same delay.
  useEffect(() => {
    if (!loggedInUserRole || !loggedInUserId) return;
    if (loggedInUserRole === USER_ROLES.CALL_CENTRE_AGENT) return;
    const timer = setTimeout(() => {
      fetchAgents(false);
      fetchIncomingSummary(false);
      fetchOutgoingCallsData(false);
      fetchAnsweredUnansweredSummary(false);
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUserRole, loggedInUserId]);

  // KPI data — fetched immediately on role resolution (independent of the
  // 3-second bootstrap so the strip populates fast). Each API returns
  // enough headline numbers to feed several KPI tiles.
  useEffect(() => {
    if (!loggedInUserRole || !loggedInUserId) return;
    if (loggedInUserRole === USER_ROLES.CALL_CENTRE_AGENT) return;
    let cancelled = false;
    setKpiLoading(true);
    Promise.allSettled([
      // shared with the Platform overview / Campaign details widgets' first load
      getApiCallShared(API_DASHBOARD_STATS),
      getApiCallShared(API_DASHBOARD_OUTBOUND_CAMPAIGNS_STATS),
      getApiCall(API_DASHBOARD_DISCONNECTED_CALL_SUMMARY),
    ]).then(([stats, camp, disc]) => {
      if (cancelled) return;
      if (stats.status === 'fulfilled') setDashboardStats(stats.value);
      if (camp.status === 'fulfilled') {
        const v: any = camp.value;
        setCampaignStats(v?.campaigns ?? v?.data?.campaigns ?? v?.data ?? v);
      }
      if (disc.status === 'fulfilled') {
        const v: any = disc.value;
        setDisconnectedSummary(v?.data ?? v);
      }
      setKpiLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loggedInUserRole, loggedInUserId]);

  const refreshIncomingOutgoing = () => {
    fetchIncomingSummary(true);
    fetchOutgoingCallsData(true);
    fetchAnsweredUnansweredSummary(true);
  };

  const refreshAgentStatusDistribution = () => fetchAgents();

  // Chart derivations (byte-identical to legacy DashboardPage).
  const value = useMemo(() => {
    const doughnutData1 = [
      { name: 'Total Agents', value: Number(agentDataObject?.total_agent) || 0 },
      { name: 'Logged In Agents', value: Number(agentDataObject?.logged_in_agent) || 0 },
      { name: 'Agents On Call', value: Number(agentDataObject?.agent_on_call) || 0 },
    ];
    const doughnutIncomingData = {
      datasets: [
        {
          data: [
            Number(incomingSummaryData.dialed_call) || 0,
            Number(incomingSummaryData.answered_call) || 0,
            Number(incomingSummaryData.abandoned_calls) || 0,
          ],
        },
      ],
    };
    const incomingData = [
      { name: 'Dialed Calls', value: Number(incomingSummaryData.dialed_call) || 0 },
      { name: 'Answered Calls', value: Number(incomingSummaryData.answered_call) || 0 },
      { name: 'Abandoned Calls', value: Number(incomingSummaryData.abandoned_calls) || 0 },
    ];
    const doughnutOutgoingData = {
      datasets: [
        {
          data: [
            Number(outgoingCallsData?.total_calls) || 0,
            Number(outgoingCallsData?.dialed_call) || 0,
            Number(outgoingCallsData?.answered_call) || 0,
          ],
        },
      ],
    };
    const outgoingDataDoughnut = [
      { name: 'Total Calls', value: Number(outgoingCallsData?.total_calls) || 0 },
      { name: 'Dialed Calls', value: Number(outgoingCallsData?.dialed_call) || 0 },
      { name: 'Answered Calls', value: Number(outgoingCallsData?.answered_call) || 0 },
    ];
    const isIncomingDataEmpty = doughnutIncomingData.datasets[0].data.every((v) => v === 0);
    const isOutgoingDataEmpty = doughnutOutgoingData.datasets[0].data.every((v) => v === 0);

    const barIncomingData = {
      series: [
        {
          name: 'Calls',
          data: [
            Number(incomingSummaryData.dialed_call) || 0,
            Number(incomingSummaryData.answered_call) || 0,
            Number(incomingSummaryData.abandoned_calls) || 0,
          ],
        },
      ],
      options: {
        chart: { type: 'bar', toolbar: { show: false } },
        grid: { padding: { top: -20, bottom: 60, left: 0, right: 0 } },
        plotOptions: { bar: { distributed: true, columnWidth: '40%' } },
        xaxis: {
          categories: ['Dialed Calls', 'Answered Calls', 'Abandoned Calls'],
          labels: { show: false },
        },
        yaxis: { labels: { offsetX: -10, style: { fontSize: '12px', fontWeight: 500 } } },
        legend: { show: false },
        colors: [AMBER_COLOR, SKY_COLOR, VIOLET_COLOR],
      },
    };
    const barOutgoingData = {
      series: [
        {
          name: 'Calls',
          data: [
            Number(outgoingCallsData?.total_calls) || 0,
            Number(outgoingCallsData?.dialed_call) || 0,
            Number(outgoingCallsData?.answered_call) || 0,
          ],
        },
      ],
      options: {
        chart: { type: 'bar', toolbar: { show: false } },
        grid: { padding: { top: -20, bottom: 60, left: 0, right: 0 } },
        plotOptions: { bar: { distributed: true, columnWidth: '40%' } },
        xaxis: {
          categories: ['Total Calls', 'Dialed Calls', 'Answered Calls'],
          labels: { show: false },
        },
        yaxis: { labels: { offsetX: -10, style: { fontSize: '12px', fontWeight: 500 } } },
        legend: { show: false },
        colors: [ROSE_COLOR, AMBER_COLOR, EMERALD_COLOR],
      },
    };
    const barAgentsData = {
      series: [
        {
          name: 'Agents',
          data: [
            Number(agentDataObject?.total_agent) || 0,
            Number(agentDataObject?.logged_in_agent) || 0,
            Number(agentDataObject?.agent_on_call) || 0,
          ],
        },
      ],
      options: {
        chart: { type: 'bar', toolbar: { show: false } },
        grid: { padding: { top: -20, bottom: 60, left: 0, right: 0 } },
        plotOptions: { bar: { distributed: true, columnWidth: '40%' } },
        xaxis: {
          categories: ['Total Agents', 'Logged In Agents', 'Agents On Call'],
          labels: { show: false },
        },
        yaxis: { labels: { offsetX: -10, style: { fontSize: '12px', fontWeight: 500 } } },
        legend: { show: false },
        colors: [EMERALD_COLOR, ROSE_COLOR, PRIMARY_COLOR],
      },
    };
    const graphData = [
      { status: 'Available', agents: agentDataObject.logged_in_agent },
      { status: 'Busy', agents: agentDataObject.agent_on_call },
      {
        status: 'Offline',
        agents: Number(agentDataObject.total_agent) - Number(agentDataObject.logged_in_agent),
      },
    ];

    return {
      startDate, endDate,
      handleDashboardStartDateChange, handleDashboardEndDateChange,
      dashboardStartCalendarMinDate, dashboardStartCalendarMaxDate,
      dashboardEndCalendarMinDate, dashboardEndCalendarMaxDate,
      handleFilter, refreshIncomingOutgoing,
      answeredUnansweredSummary, answeredUnansweredSummaryLoading,
      graphData, loadingForAgentStatusDistribution, refreshAgentStatusDistribution,
      barAgentsData, doughnutData1, barIncomingData, incomingData,
      isIncomingDataEmpty, barOutgoingData, outgoingDataDoughnut, isOutgoingDataEmpty,
      // KPI data
      dashboardStats, campaignStats, disconnectedSummary, kpiLoading,
      agentDataObject, incomingSummaryData, outgoingCallsData,
    };
  }, [
    startDate, endDate,
    agentDataObject, incomingSummaryData, outgoingCallsData,
    answeredUnansweredSummary, answeredUnansweredSummaryLoading,
    loadingForAgentStatusDistribution,
    dashboardStats, campaignStats, disconnectedSummary, kpiLoading,
  ]);

  return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>;
}
